export interface RoomType {
  name: string;
  slug: string;
  description: string;
  longDescription: string;
  /** 2–3 sentences of unique, room-specific guidance shown on the detail page. */
  designTips: string;
  /** "What to look for" / common mistakes — unique copy per room. */
  whatToConsider: string;
  /** Slugs of style categories that work best for this room (from DESIGN_CATEGORIES.id). */
  bestCategoryIds: string[];
}

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\//g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const r = (
  name: string,
  description: string,
  longDescription: string,
  designTips: string,
  whatToConsider: string,
  bestCategoryIds: string[],
): RoomType => ({
  name,
  slug: slug(name),
  description,
  longDescription,
  designTips,
  whatToConsider,
  bestCategoryIds,
});

export const ROOM_TYPES_DATA: RoomType[] = [
  r(
    "Floor Plan / 3D Render",
    "Style architectural floor plans and 3D interior renders in any aesthetic.",
    "Upload a 2D floor plan, blueprint, or 3D interior render and Decor Canvas will produce a photorealistic styled visualization. The AI preserves your exact layout — walls, doors, windows, and fixed fixtures — and styles each zone according to its function while applying the design style you choose.",
    "Use clean, high-contrast plans or renders for best results — labels on zones (Bedroom, Kitchen, etc.) help the AI style each area appropriately. Pair with a specific style (e.g. Modern Luxe, Japandi) and add custom ideas to guide materials, palette, and mood.",
    "Very low-resolution or heavily annotated CAD drawings can confuse the renderer. If a zone isn't labeled, the AI infers function from fixtures — so include beds, sofas, or kitchen counters in the source for clarity.",
    ["classic-global", "trend-forward", "luxury-glam", "regional-indian"],
  ),
  r(
    "Living Room",
    "Reimagine your living room with AI-powered design.",
    "Upload a photo of your living room and instantly visualize it in 90+ design styles — from Scandinavian minimalism to Punjabi Haveli grandeur. Decor Canvas's AI keeps your walls, windows, and architecture intact while transforming furniture, color palette, lighting, and accents.",
    "Living rooms work best when the seating arrangement frames a clear focal point — a TV unit, fireplace, or feature wall. Layered lighting (ambient ceiling + task floor lamps + accent table lamps) makes any style feel intentional. For Indian apartments, swivel chairs and a low coffee table open the room for floor seating during festivals.",
    "Watch the rug size — most rugs people buy are one size too small. The front legs of every seat should sit on the rug. Also keep at least 75 cm of walking space between the sofa and coffee table.",
    ["classic-global", "trend-forward", "casual-comfortable", "luxury-glam", "regional-indian"],
  ),
  r(
    "Bedroom",
    "Design a bedroom that matches your dream aesthetic.",
    "Visualize your bedroom in soothing Japandi, opulent Hollywood Glam, or cozy Modern Farmhouse. The AI preserves window placement and room geometry while restyling beds, lighting, textiles, and finishes.",
    "Anchor the bed on the longest unbroken wall so both bedside tables fit symmetrically. Use a warm 2700K bedside lamp instead of overhead lights for evenings — it drops cortisol and signals sleep. Layer a flatweave rug under the lower two-thirds of the bed for that hotel-bedroom feel.",
    "Avoid placing the headboard against a window when possible — it wrecks insulation and acoustics. If you must, use a tall padded headboard to compensate.",
    ["casual-comfortable", "luxury-glam", "nature-biophilic", "dark-moody", "wellness-spa"],
  ),
  r(
    "Kids Room / Nursery",
    "Playful, safe, and imaginative kids' rooms.",
    "Whimsical Storybook, Adventure Safari, Pastel Dreamland and more — generate child-friendly room designs that grow with your little one.",
    "Design in zones: sleep, play, and a small study nook from age 4 onwards. Use washable matte paint (not gloss) on the lower 1 m of walls — it survives crayons and sticky fingers. Keep the colour palette to three muted shades plus one bold accent; busy walls make sleep harder.",
    "Skip heavy ceiling fixtures over the crib or bed. Anchor every tall dresser to the wall — toddlers climb everything.",
    ["kids-playful", "casual-comfortable", "nature-biophilic"],
  ),
  r(
    "Kitchen",
    "AI kitchen design — modular, modern, traditional or rustic.",
    "From Indian modular kitchens to Modern Farmhouse and Industrial Cafe layouts, see your kitchen reimagined with new cabinets, backsplashes, countertops, and lighting.",
    "Plan around the work triangle — sink, hob, fridge should each be 1.2–2.7 m apart. Quartz beats marble for Indian cooking (turmeric and lemon stain marble in days). Under-cabinet LED strips matter more than ceiling lights for actual prep work.",
    "Don't run upper cabinets all the way to a tall ceiling unless you genuinely use the top shelf — it dates fast. A continuous backsplash from counter to underside of upper cabinets reads cleaner than tiled wainscot.",
    ["classic-global", "trend-forward", "casual-comfortable", "regional-indian", "futuristic-tech"],
  ),
  r(
    "Dining Room",
    "Restyle your dining room for everyday meals or grand entertaining.",
    "From minimalist Scandinavian dining to Mughal Revival opulence, generate AI dining room concepts with new furniture, lighting fixtures, and wall treatments.",
    "Hang the pendant 75–90 cm above the table — measure to the bottom of the fixture. The pendant width should be roughly half the table width. Leave 90 cm of clearance behind every chair for pull-out room.",
    "Round tables seat more people in tight rooms and remove the awkward 'head of the table' hierarchy. Avoid glass tabletops in homes with kids — they show every fingerprint.",
    ["classic-global", "luxury-glam", "traditional-cultural", "vintage-retro"],
  ),
  r(
    "Study Room",
    "Focused, productive study room and home library designs.",
    "Dark Academia, Smart Minimalism, or Mid-Century Modern — design a study that helps you focus, complete with shelves, desks, and ambient lighting.",
    "Position the desk perpendicular to the window, not facing or back-to it — perpendicular kills both screen glare and silhouette shadows on video calls. Add a 4000K task lamp for reading; pair it with warmer ambient light to avoid eye strain.",
    "Open shelves look beautiful but collect dust quickly. Mix 70% closed cabinets with 30% open display for low-maintenance styling.",
    ["dark-moody", "trend-forward", "vintage-retro"],
  ),
  r(
    "Bathroom",
    "Spa-quality bathroom redesigns in seconds.",
    "Onsen-inspired, Modern Luxe, Coastal, or Wabi-Sabi — visualize new tile, vanities, fixtures, and lighting before you renovate.",
    "Large-format tiles (60×120 cm and up) read more luxurious and create fewer grout lines to scrub. Add a niche inside the shower wall — it removes the need for ugly corner caddies. Two sconces flanking the mirror beat one overhead light for makeup and shaving.",
    "Avoid pure white grout with white tile — it yellows. Use a warm grey grout instead; it ages gracefully and disguises soap film.",
    ["wellness-spa", "luxury-glam", "nature-biophilic", "classic-global"],
  ),
  r(
    "Office",
    "Productive home office and workspace designs.",
    "Scandinavian Co-working, Industrial, or Boutique Hotel — generate office layouts that balance focus and aesthetic.",
    "Pick a chair before you pick a desk — your spine matters more than your aesthetic. A 140 cm desk is the sweet spot for a single monitor plus laptop. Add one biophilic element (plant, wood grain, natural fiber rug) to lower visual fatigue during long calls.",
    "Acoustic panels disguised as art absorb call echo without screaming 'home office'. Cable trays under the desk make the entire room read 10× cleaner.",
    ["trend-forward", "futuristic-tech", "commercial-hospitality"],
  ),
  r(
    "Home Theater / Media Room",
    "Cinematic media room concepts powered by AI.",
    "Velvet Speakeasy, Noir Modern, or Cyberpunk — design immersive media rooms with the perfect mood lighting and seating.",
    "Paint walls and ceiling a deep matte charcoal or navy — it eliminates reflected light and improves perceived contrast. Tiered seating only pays off if your room is 5 m or longer; otherwise a deep single-row sectional gives a better experience. Add bias lighting behind the screen to reduce eye strain.",
    "Avoid recessed ceiling spots aimed at the screen. Use dimmable wall sconces or LED strips along the floor instead.",
    ["dark-moody", "luxury-glam", "futuristic-tech"],
  ),
  r(
    "Gaming Room",
    "RGB-rich gaming setups and battlestations.",
    "Cyberpunk, Space-Age Retro, or Smart Minimalism gaming spaces — visualize lighting, wall art, and ergonomic layouts.",
    "Three lighting layers make a gaming room photograph well: ambient (bias light behind monitor), accent (RGB strips on the desk edge or wall), and key (a soft sidelight on your face for streaming). A 27\" or 32\" monitor with a curved 1000R panel is the modern baseline.",
    "RGB everywhere reads chaotic on camera. Lock to two accent colours that match your stream branding.",
    ["futuristic-tech", "dark-moody", "kids-playful"],
  ),
  r(
    "Walk-in Closet",
    "Boutique-style walk-in closet design.",
    "Parisian Chic, Modern Luxe, or Hollywood Glam closet designs with custom shelving and island layouts.",
    "Mix open shelving for shoes and folded knits with closed drawers for delicates. A 90 cm island in the centre needs at least 90 cm clearance on all sides. Add a full-length mirror on a long blank wall and use 3000K LED strip lighting inside hanging sections.",
    "Don't dedicate more than 40% of hanging space to long-hang — most wardrobes need double-hang for shirts and trousers.",
    ["luxury-glam", "classic-global", "trend-forward"],
  ),
  r(
    "Laundry / Utility Room",
    "Functional laundry rooms that look beautiful.",
    "Modern Farmhouse, Scandinavian, or Coastal utility rooms with smart storage and bright finishes.",
    "Stack the washer and dryer to free a full counter for folding — it's the single highest-ROI move in a utility room. A deep utility sink handles muddy shoes, paint trays, and pet baths. Quartz or sealed laminate beats wood here; humidity kills timber.",
    "Vent the dryer to the exterior, never into the room — humidity wrecks paint and grows mould.",
    ["casual-comfortable", "trend-forward"],
  ),
  r(
    "Garage",
    "Workshop, gym, or showroom garage redesigns.",
    "Industrial, Neo-Industrial, or Vintage Industrial garages with epoxy floors, lighting, and storage walls.",
    "An epoxy floor (with anti-slip flake) is the single change that transforms a garage. Slatwall panels along one wall flex between tools, sports gear, and seasonal storage. Use 5000K LED shop lights — anything warmer makes paintwork and engines look muddy.",
    "Leave at least 90 cm of door-swing clearance around the car when planning storage walls.",
    ["futuristic-tech", "dark-moody", "vintage-retro"],
  ),
  r(
    "Gym",
    "Home gym design with motivating aesthetics.",
    "Industrial, Smart Minimalism, or Wellness Clinic home gyms with mirrors, rubber flooring, and ambient lighting.",
    "20 mm rubber tiles protect floors from dropped plates and soften joints. Mirror at least one full wall — visual form-checking matters as much as motivation. Position any cardio machine to face natural light, not the wall.",
    "Skip carpet anywhere near a squat rack — bars leave permanent dents. Plan a ceiling height of at least 2.4 m for overhead presses.",
    ["futuristic-tech", "wellness-spa", "trend-forward"],
  ),
  r(
    "Balcony",
    "Indian balcony design — small spaces, big style.",
    "Tropical Resort, Bohemian, or Urban Jungle balcony makeovers — perfect for Indian apartments.",
    "Vertical planters multiply usable greenery without sacrificing floor space. A folding bistro set seats two and tucks against the railing when not in use. Outdoor rugs in polypropylene survive monsoon and direct sun.",
    "Check your society's rules on railing planters and string lights before you buy. Use UV-rated cushions; regular indoor fabric fades in weeks.",
    ["nature-biophilic", "regional-indian", "casual-comfortable"],
  ),
  r(
    "Patio / Terrace / Rooftop",
    "Outdoor terrace and rooftop styling.",
    "Mediterranean, Tropical Resort, or Tuscan terraces with planters, lighting, and seating arrangements.",
    "Define zones: a dining area near the kitchen exit, lounge seating around a focal point, and a planted boundary for privacy. Festoon string lights at 2.4 m height read warmer than ground floods. Use teak, powder-coated aluminium, or polywood — anything else degrades in two seasons.",
    "Always check waterproofing and load-bearing limits before adding heavy planters or water features to a terrace.",
    ["nature-biophilic", "classic-global", "wellness-spa"],
  ),
  r(
    "Garden / Backyard",
    "Backyard and garden landscape concepts.",
    "Biophilic, Desert Modern, or Forest Bathing gardens with hardscapes, plant palettes, and feature lighting.",
    "Layer plants by height: groundcover, mid-height shrubs, then a tall anchor tree or structural grass. Curved bed edges read more naturalistic than straight lines. Path lighting at ankle height beats post lights for both safety and atmosphere.",
    "Native and climate-appropriate plants slash water use by 40–60% and need less maintenance.",
    ["nature-biophilic", "casual-comfortable", "wellness-spa"],
  ),
  r(
    "Foyer / Entryway",
    "Make a memorable first impression with AI entryway designs.",
    "Art Deco, Indian Traditional, or Hollywood Glam entryways with statement lighting, console tables, and mirrors.",
    "A console table 75–90 cm wide with a mirror above it sets the tone in under 60 cm of depth. Add a hidden tray for keys, a low bench for shoe changes, and one statement light. The entryway is where you can be loudest with pattern — guests pass through in seconds.",
    "Avoid placing the entryway mirror directly opposite the front door if you follow Vastu — instead, angle it on the adjacent wall.",
    ["classic-global", "luxury-glam", "traditional-cultural", "indian-traditional"],
  ),
  r(
    "Hallway",
    "Reimagine narrow hallways into design statements.",
    "Gallery wall hallways, Dark Academia corridors, or Coastal pass-throughs with the right runners and sconces.",
    "Narrow hallways shine with a runner rug, picture-rail gallery, and wall sconces every 1.8 m. Pale ceilings and slightly darker walls visually widen a corridor. Hang frames at consistent centre-height (145 cm) for a museum-grade rhythm.",
    "Avoid pendant lights in a hallway under 2.4 m ceiling — they create head-clearance issues and visual clutter.",
    ["dark-moody", "vintage-retro", "classic-global"],
  ),
  r(
    "Reception / Waiting Area",
    "Reception and waiting area designs for any business.",
    "Boutique Hotel, Wellness Clinic, or Scandinavian Co-working receptions that welcome customers.",
    "Sightline matters most — a guest should see the reception desk within two seconds of entering. Mix seating types (lounge chairs, a bench, one high stool) for different body types and wait lengths. Background music at 55–65 dB measurably lowers perceived wait time.",
    "Avoid hard parallel walls in waiting areas — they amplify reception phone noise. Use acoustic panels disguised as wall art.",
    ["commercial-hospitality", "wellness-spa", "trend-forward"],
  ),
  r(
    "Cafe / Restaurant",
    "Cafe, bistro, and restaurant interior design.",
    "Industrial Cafe, Speakeasy Lounge, Italian Renaissance, or Japanese Ryokan dining — restaurant concepts in seconds.",
    "Two-tops on banquettes, four-tops in the middle, six-tops near walls — this is the standard FOH grid. Plan 1.4 sq m per cover for casual dining, 1.8+ for fine dining. Lighting should hit the food, not the diner's face: low pendants over each table at 75 cm above the surface.",
    "Hard surfaces everywhere create a wall of noise. Mix in upholstered banquettes, fabric ceiling baffles, or felt wall panels to keep conversation possible.",
    ["commercial-hospitality", "vintage-retro", "dark-moody", "traditional-cultural"],
  ),
  r(
    "Retail Store",
    "AI-powered retail store interior concepts.",
    "Boutique Hotel, Modern Luxe, Memphis, or Maximalist retail interiors with lighting, fixtures, and merchandising flow.",
    "Plan a 'decompression zone' of 1.5 m of empty floor right inside the entrance — shoppers ignore product placed there. Use the right-turn rule: most shoppers turn right on entry, so place your highest-margin product on the right wall. Track lighting with 3000K accent spots makes product colour pop.",
    "Mirror placement near the fitting rooms drives more conversion than mirrors near the entrance.",
    ["commercial-hospitality", "luxury-glam", "trend-forward"],
  ),
  r(
    "Co-working Space",
    "Inspiring co-working and shared workspace designs.",
    "Scandinavian Co-working, Urban Modern, or Industrial — generate flexible workspaces with hot desks, phone booths, and lounges.",
    "Mix three zones at minimum: focus desks (quiet), collaborative tables (medium), and lounge or phone booths (loud or private). Acoustic separation matters more than visual separation — felt baffles and rugs do more than half-height partitions. Provide one phone booth per 15 members.",
    "Always over-spec power and data. Members notice missing outlets within minutes; missing aesthetics take weeks.",
    ["commercial-hospitality", "trend-forward", "futuristic-tech"],
  ),
];

export const getRoomBySlug = (s: string) => ROOM_TYPES_DATA.find((rt) => rt.slug === s);
